import type { AccountInfo, AuthenticationResult, TenantProfile } from '@azure/msal-browser';

type SerializedAccount = Omit<AccountInfo, 'tenantProfiles'> & {
    tenantProfiles?: Array<[string, TenantProfile]>;
};

export type SerializedAuthenticationResult = Omit<AuthenticationResult,
    'account' | 'expiresOn' | 'extExpiresOn' | 'refreshOn'> & {
    account: SerializedAccount;
    expiresOn: number | null;
    extExpiresOn?: number;
    refreshOn?: number;
};

export function serializeAuthenticationResult(result: AuthenticationResult): SerializedAuthenticationResult
{
    const claimExpiry = (result.idTokenClaims as { exp?: unknown })?.exp;
    const idTokenExpiry = typeof claimExpiry === 'number' && Number.isFinite(claimExpiry)
        ? claimExpiry * 1000 : null;
    const expiresOn = result.expiresOn?.getTime() ?? idTokenExpiry;
    return {
        ...result,
        account: {
            ...result.account,
            tenantProfiles: result.account.tenantProfiles
                ? Array.from(result.account.tenantProfiles.entries()) : undefined,
        },
        // ID-only login results can have no access-token expiry. MSAL's cache
        // still needs a deadline to return the selected ID token after reopen.
        expiresOn,
        extExpiresOn: result.extExpiresOn?.getTime() ?? expiresOn ?? undefined,
        refreshOn: result.refreshOn?.getTime(),
    };
}

export function deserializeAuthenticationResult(result: SerializedAuthenticationResult): AuthenticationResult
{
    return {
        ...result,
        account: {
            ...result.account,
            tenantProfiles: result.account.tenantProfiles
                ? new Map(result.account.tenantProfiles) : undefined,
        },
        expiresOn: result.expiresOn === null ? null : new Date(result.expiresOn),
        extExpiresOn: result.extExpiresOn === undefined ? undefined : new Date(result.extExpiresOn),
        refreshOn: result.refreshOn === undefined ? undefined : new Date(result.refreshOn),
    };
}
